import { useEffect, useState } from 'react';
import { useFollowUpsStore } from '../stores/followupsStore';
import { useAuthStore } from '../stores/authStore';
import { EmptyState } from '../components/EmptyState';
import { Toast } from '../components/Toast';
import { formatDateTime, isOverdue, type FollowUp } from '../types';

export function FollowUpsPage() {
  const { followups, pendingCount, isLoading, error, fetchFollowUps, fetchPendingCount, fetchDueToday, createFollowUp, updateFollowUp, deleteFollowUp } = useFollowUpsStore();
  const { token } = useAuthStore();
  const [showForm, setShowForm] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState<FollowUp | null>(null);
  const [filterStatus, setFilterStatus] = useState<FollowUp['status'] | ''>('');
  const [dueToday, setDueToday] = useState<FollowUp[]>([]);
  const [formData, setFormData] = useState({
    title: '', description: '', dueDate: '', dueTime: '', recurrence: null as 'daily' | 'weekly' | 'monthly' | null,
    memoryId: '', conversationId: '', result: '', learning: '',
  });

  useEffect(() => {
    if (token) {
      fetchFollowUps({ status: filterStatus || undefined });
      fetchPendingCount();
      fetchDueToday().then(setDueToday);
    }
  }, [token, filterStatus, fetchFollowUps, fetchPendingCount, fetchDueToday]);

  const [mutationError, setMutationError] = useState<string | null>(null);
  const refresh = async () => {
    await Promise.all([fetchFollowUps({ status: filterStatus || undefined }), fetchPendingCount(), fetchDueToday().then(setDueToday)]);
  };
  const mutate = async (operation: () => Promise<void>) => {
    setMutationError(null);
    try { await operation(); await refresh(); return true; }
    catch (e) { setMutationError((e as Error).message); return false; }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const dueDate = formData.dueDate ? new Date(`${formData.dueDate}T${formData.dueTime || '00:00'}`).getTime() : null;
    const data = {
      title: formData.title,
      description: formData.description || null,
      dueDate,
      recurrence: formData.recurrence,
      ...(!editingFollowUp || formData.result !== (editingFollowUp.result || '') ? { result: formData.result.trim() || null } : {}),
      ...(!editingFollowUp || formData.learning !== (editingFollowUp.learning || '') ? { learning: formData.learning.trim() || null } : {}),
      memoryId: formData.memoryId || null,
      conversationId: formData.conversationId || null,
    };
    if (!await mutate(() => editingFollowUp ? updateFollowUp(editingFollowUp.id, data) : createFollowUp({ ...data, status: 'pending' }))) return;
    setShowForm(false);
    setEditingFollowUp(null);
    resetForm();
  };

  const handleEdit = (fu: FollowUp) => {
    setEditingFollowUp(fu);
    const dueDate = fu.dueDate ? new Date(fu.dueDate) : null;
    setFormData({
      title: fu.title,
      description: fu.description || '',
      dueDate: dueDate ? (`${dueDate.getFullYear()}-${String(dueDate.getMonth() + 1).padStart(2, '0')}-${String(dueDate.getDate()).padStart(2, '0')}`) : '',
      dueTime: dueDate ? dueDate.toTimeString().slice(0, 5) : '',
      recurrence: fu.recurrence,
      memoryId: fu.memoryId || '',
      conversationId: fu.conversationId || '',
      result: fu.result || '', learning: fu.learning || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Excluir este follow-up?')) await mutate(() => deleteFollowUp(id));
  };

  const handleToggleDone = async (fu: FollowUp) => {
    await mutate(() => updateFollowUp(fu.id, { status: fu.status === 'done' ? 'pending' : 'done' }));
  };

  const resetForm = () => setFormData({ title: '', description: '', dueDate: '', dueTime: '', recurrence: null, memoryId: '', conversationId: '', result: '', learning: '' });

  const statusLabel: Record<FollowUp['status'], string> = { pending: 'Pendente', done: 'Concluído', cancelled: 'Cancelado' };
  const statusColor: Record<FollowUp['status'], string> = { pending: 'var(--warning)', done: 'var(--success)', cancelled: 'var(--fg-muted)' };

  if (!token) return <EmptyState title="Configure seu token" description="Vá em Configurações para definir seu token de acesso." />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, padding: '1rem' }}>
      {(mutationError || error) && <Toast message={mutationError || error!} onClose={() => setMutationError(null)} />}

      <header style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>Follow-ups</h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--fg-muted)' }}>{pendingCount} pendentes</p>
          </div>
          <button className="btn btn-primary" onClick={() => { resetForm(); setEditingFollowUp(null); setShowForm(true); }}>+ Novo</button>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {(['', 'pending', 'done', 'cancelled'] as const).map(status => (
            <button
              key={status}
              className={`tab ${filterStatus === status ? 'active' : ''}`}
              onClick={() => setFilterStatus(status)}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              {status ? statusLabel[status] : 'Todos'}
            </button>
          ))}
        </div>
      </header>

      {dueToday.length > 0 && (
        <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'rgba(251, 191, 36, 0.1)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)' }}>
          <div style={{ fontWeight: 600, color: 'var(--warning)', marginBottom: '0.5rem' }}>⚠️ {dueToday.length} follow-up(s) com prazo hoje</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {dueToday.map(fu => (
              <span key={fu.id} className="badge" style={{ background: 'var(--warning)', color: 'var(--bg)' }}>
                {fu.title}
              </span>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-backdrop" onClick={() => { setShowForm(false); setEditingFollowUp(null); resetForm(); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1rem' }}>{editingFollowUp ? 'Editar Follow-up' : 'Novo Follow-up'}</h3>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>Título *</label>
                <input value={formData.title} onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))} required placeholder="Título da ação" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>Descrição</label>
                <textarea value={formData.description} onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))} rows={2} placeholder="Detalhes..." />
              </div>
              {(['result', 'learning'] as const).map(field => (
                <div key={field}>
                  <label htmlFor={field} style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>{field === 'result' ? 'Resultado confirmado' : 'Aprendizado confirmado'}</label>
                  <textarea id={field} value={formData[field]} onChange={e => setFormData(prev => ({ ...prev, [field]: e.target.value }))} rows={2} maxLength={4000} placeholder={field === 'result' ? 'O que aconteceu de fato?' : 'O que aprendemos com este caso?'} />
                </div>
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.5rem', alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>Data</label>
                  <input type="date" value={formData.dueDate} onChange={e => setFormData(prev => ({ ...prev, dueDate: e.target.value }))} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>Hora</label>
                  <input type="time" value={formData.dueTime} onChange={e => setFormData(prev => ({ ...prev, dueTime: e.target.value }))} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>Repetir</label>
                  <select value={formData.recurrence || ''} onChange={e => setFormData(prev => ({ ...prev, recurrence: e.target.value as any }))}>
                    <option value="">Não repetir</option>
                    <option value="daily">Diário</option>
                    <option value="weekly">Semanal</option>
                    <option value="monthly">Mensal</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setEditingFollowUp(null); resetForm(); }}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={!formData.title}>{editingFollowUp ? 'Salvar' : 'Criar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <div className="empty-state"><div className="spinner" style={{ width: '24px', height: '24px' }} /></div>
        ) : followups.length === 0 ? (
          <EmptyState title="Nenhum follow-up" description="Crie follow-ups para acompanhar decisões e ações." icon="📋" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {followups.map(fu => (
              <div key={fu.id} className="card followup-card" style={{ display: 'flex', alignItems: 'start', gap: '0.75rem', padding: '1rem', flexWrap: 'wrap' }}>
                <input
                  type="checkbox"
                  aria-label={`${fu.status === 'done' ? 'Reabrir' : 'Concluir'} ${fu.title}`}
                  checked={fu.status === 'done'}
                  onChange={() => handleToggleDone(fu)}
                  style={{ width: '20px', height: '20px', accentColor: 'var(--success)' }}
                />
                <div style={{ flex: '1 1 200px', minWidth: 0, overflowWrap: 'anywhere' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <h4 style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{fu.title}</h4>
                    <span className="badge" style={{ background: statusColor[fu.status] }}>{statusLabel[fu.status]}</span>
                    {fu.recurrence && <span className="badge" style={{ background: 'var(--accent)' }}>{fu.recurrence}</span>}
                    {fu.dueDate && (
                      <span className="badge" style={{ background: isOverdue(fu.dueDate) && fu.status !== 'done' ? 'var(--error)' : 'var(--accent)' }}>
                        📅 {formatDateTime(fu.dueDate)}
                      </span>
                    )}
                  </div>
                  {fu.description && <p style={{ fontSize: '0.8125rem', color: 'var(--fg-muted)', marginTop: '0.25rem' }}>{fu.description}</p>}
                  {fu.result && <p style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', marginTop: '0.5rem' }}><strong>Resultado:</strong> {fu.result}</p>}
                  {fu.learning && <p style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', marginTop: '0.5rem' }}><strong>Aprendizado:</strong> {fu.learning}</p>}
                </div>
                <div style={{ display: 'flex', gap: '0.375rem' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleEdit(fu)}>Editar</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => handleDelete(fu.id)}>Excluir</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 200; padding: 1rem; }
        .modal { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--radius); padding: 1.5rem; width: 100%; max-width: 500px; max-height: 85dvh; overflow: auto; }
        .spinner { border: 2px solid var(--border); border-top-color: var(--primary); border-radius: 50%; animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}